import { useCallback, useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthApi } from "../../api/common/apiIndex";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import BusinessDetailsForm from "./BusinessDetailsForm";
import OwnerRegistrationForm from "./OwnerRegistrationForm";
import { MdChevronRight, MdCheckCircle } from "react-icons/md";
import showConfirmToast from "../business/businessUtilities/ConfirmToast";
import OnSuccess from "../common/commonUtils/OnSuccess";
import PublicShell from "../common/shell/PublicShell";
import "./LoginForm.css";

export default function PublicBusinessRegistration() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1); // 1 = business, 2 = owner, 3 = success
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [nameAvailability, setNameAvailability] = useState({
    isChecking: false,
    isTaken: false,
  });

  const [biz, setBiz] = useState({
    name: "",
    country: "",
  });

  const isNonEmpty = (v) => !!v && String(v).trim() !== "";
  const bizDirty =
    isNonEmpty(biz.name) ||
    isNonEmpty(biz.country);

  const isDirty = (bizDirty || acceptTerms || newsletterOptIn || step === 2) && step !== 3;

  const bizValid = useMemo(
    () =>
      Boolean(
        biz.name?.trim() &&
        biz.country?.trim() &&
        acceptTerms &&
        !nameAvailability.isChecking &&
        !nameAvailability.isTaken
      ),
    [biz.name, biz.country, acceptTerms, nameAvailability.isChecking, nameAvailability.isTaken]
  );

  const handleNameAvailabilityChange = useCallback((next) => {
    setNameAvailability({
      isChecking: Boolean(next?.isChecking),
      isTaken: Boolean(next?.isTaken),
    });
  }, []);

  const registerMutation = useMutation({
    mutationFn: (payload) => AuthApi.registerBusiness(payload),
    onSuccess: () => {
      setStep(3);
      setBiz({ name: "", country: "" });
      setAcceptTerms(false);
      setNewsletterOptIn(false);
    },
  });

  const registerBusiness = async (ownerPayload) => {
    if (!bizValid) {
      setStep(1);
      throw new Error(nameAvailability.isTaken
        ? "This business name is already taken."
        : "Please complete the business details and accept the terms.");
    }

    const ownerName = `${ownerPayload.firstName} ${ownerPayload.lastName}`.trim();
    const payload = {
      business: {
        name: biz.name.trim(),
        country: biz.country.trim(),
        contactName: ownerName,
        contactEmail: ownerPayload.email,
        contactPhone: ownerPayload.phone,
      },
      owner: {
        firstName: ownerPayload.firstName,
        lastName: ownerPayload.lastName,
        country: ownerPayload.country || biz.country.trim(),
        email: ownerPayload.email,
        password: ownerPayload.password,
        phone: ownerPayload.phone,
      },
      acceptTerms: true,
      newsletterOptIn,
    };
    await registerMutation.mutateAsync(payload);
    queryClient.invalidateQueries({ queryKey: [] });
  };

  const StepChip = ({ active, done, children }) => (
    <span
      className={`badge rounded-pill ${done ? "text-bg-success" : active ? "text-bg-primary" : "bg-body border text-body"
        } border`}
    >
      {children} {done ? <MdCheckCircle style={{ verticalAlign: "-2px" }} /> : null}
    </span>
  );

  const handleBackClick = (e) => {
    if (!isDirty) return;
    e.preventDefault();
    showConfirmToast({
      id: "leave-reg-confirm",
      message: "You have unfinished registration. Do you still want to leave?",
      onConfirm: () => navigate("/login"),
      onCancel: () => { },
    });
  };
  return (
    <PublicShell className="login-light business-register-shell" size="wide" brandOnClick={handleBackClick}>
      <div className="mx-auto business-register__frame">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            <div className="card rounded-4 border-0 shadow-sm">
              <div className="card-header">
                <div className="d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                  <div className="d-none d-md-flex align-items-center gap-2">
                    <StepChip active={step === 1} done={step > 1}>Business</StepChip>
                    <MdChevronRight />
                    <StepChip active={step === 2} done={step > 2}>Owner</StepChip>
                    <MdChevronRight />
                    <StepChip active={step === 3} done={false}>Done</StepChip>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="business-register__step">
                  {step === 1 && (
                    <>
                      <div className="row g-3">
                        <div className="col-12">
                          <BusinessDetailsForm
                            value={biz}
                            onChange={setBiz}
                            onNameAvailabilityChange={handleNameAvailabilityChange}
                          />
                        </div>
                      </div>

                      <div className="form-check mt-3">
                        <input
                          id="acceptTerms"
                          className="form-check-input"
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="acceptTerms">
                          I accept the Terms and Privacy Policy
                        </label>
                      </div>

                      <div className="form-check mt-2">
                        <Link to={"/privacy-policy"} className="font-sm">Terms & privacy policy</Link>
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <div className="business-register__owner">
                      <OwnerRegistrationForm
                        businessCountry={biz.country}
                        submitting={registerMutation.isPending}
                        onBack={() => setStep(1)}
                        onSubmit={registerBusiness}
                      />
                    </div>
                  )}

                  {step === 3 && (
                    <OnSuccess
                      message="Thank you for registering your business with Baknay!"
                      details="We’ve received your application and will review it as soon as possible. You’ll be notified once your account is ready. Click Done to go back to login."
                      onDone={() => navigate("/login")}
                    />
                  )}
                </div>
              </div>

              <div className="card-footer d-flex justify-content-end gap-2">
                {step === 1 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (!biz.name?.trim() || !biz.country?.trim()) {
                        toast.warn("Please fill in Business name and Country.");
                        return;
                      }
                      if (!acceptTerms) {
                        toast.info("Please accept the terms to continue.");
                        return;
                      }
                      if (nameAvailability.isTaken) {
                        toast.warn("This business name is already taken.");
                        return;
                      }
                      if (nameAvailability.isChecking) {
                        toast.info("Please wait while we check the business name.");
                        return;
                      }
                      setStep(2);
                    }}
                    disabled={!bizValid}
                  >
                    Next: Owner details
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    Back to business
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate("/login")}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
